/**
 * Client-side rate limiting utility
 * Prevents excessive API calls from the frontend
 */

class RateLimiter {
  constructor() {
    this.requests = new Map();
    this.defaultLimits = {
      auth: { maxRequests: 5, windowMs: 60000 }, // 5 requests per minute for auth
      api: { maxRequests: 100, windowMs: 60000 }, // 100 requests per minute for general API
      session: { maxRequests: 20, windowMs: 60000 }, // 20 requests per minute for session operations
    };
  }

  /**
   * Check if a request is allowed based on rate limits
   * @param {string} key - Unique identifier for the rate limit (e.g., 'auth', 'api', 'session')
   * @param {Object} options - Rate limit options
   * @returns {boolean} - Whether the request is allowed
   */
  isAllowed(key, options = {}) {
    const now = Date.now();
    const limit = { ...this.defaultLimits[key] || this.defaultLimits.api, ...options };
    
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    
    const requests = this.requests.get(key);
    
    // Remove old requests outside the window
    const windowStart = now - limit.windowMs;
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    
    // Check if we're under the limit
    if (validRequests.length < limit.maxRequests) {
      validRequests.push(now);
      this.requests.set(key, validRequests);
      return true;
    }
    
    return false;
  }

  /**
   * Get the time until the next request is allowed
   * @param {string} key - Rate limit key
   * @param {Object} options - Rate limit options
   * @returns {number} - Milliseconds until next request is allowed
   */
  getTimeUntilReset(key, options = {}) {
    const now = Date.now();
    const limit = { ...this.defaultLimits[key] || this.defaultLimits.api, ...options };
    
    if (!this.requests.has(key)) {
      return 0;
    }
    
    const requests = this.requests.get(key);
    const windowStart = now - limit.windowMs;
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    
    if (validRequests.length < limit.maxRequests) {
      return 0;
    }
    
    // Return time until the oldest request expires
    const oldestRequest = Math.min(...validRequests);
    return (oldestRequest + limit.windowMs) - now;
  }

  /**
   * Clear rate limit for a specific key
   * @param {string} key - Rate limit key to clear
   */
  clear(key) {
    this.requests.delete(key);
  }

  /**
   * Clear all rate limits
   */
  clearAll() {
    this.requests.clear();
  }

  /**
   * Get current rate limit status
   * @param {string} key - Rate limit key
   * @param {Object} options - Rate limit options
   * @returns {Object} - Rate limit status
   */
  getStatus(key, options = {}) {
    const now = Date.now();
    const limit = { ...this.defaultLimits[key] || this.defaultLimits.api, ...options };
    
    if (!this.requests.has(key)) {
      return {
        allowed: true,
        remaining: limit.maxRequests,
        resetTime: 0
      };
    }
    
    const requests = this.requests.get(key);
    const windowStart = now - limit.windowMs;
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    const remaining = Math.max(0, limit.maxRequests - validRequests.length);
    const resetTime = this.getTimeUntilReset(key, options);
    
    return {
      allowed: remaining > 0,
      remaining,
      resetTime
    };
  }
}

// Create a singleton instance
const rateLimiter = new RateLimiter();

export default rateLimiter;

// Helper function to create a rate-limited API call
export const createRateLimitedCall = (key, options = {}) => {
  return async (apiCall) => {
    if (!rateLimiter.isAllowed(key, options)) {
      const resetTime = rateLimiter.getTimeUntilReset(key, options);
      throw new Error(`Rate limit exceeded. Please wait ${Math.ceil(resetTime / 1000)} seconds before trying again.`);
    }
    
    return apiCall();
  };
};

// Helper function to show rate limit status to user
export const getRateLimitMessage = (key, options = {}) => {
  const status = rateLimiter.getStatus(key, options);
  
  if (status.allowed) {
    return null;
  }
  
  const resetSeconds = Math.ceil(status.resetTime / 1000);
  return `Too many requests. Please wait ${resetSeconds} seconds before trying again.`;
};
